import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Lock, Plus, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { usePractice } from "@/pages/StaffApp";

function NewRole({ roles, onAdded }) {
  const [label, setLabel] = useState("");
  const [copy, setCopy] = useState("none");
  const add = async () => {
    try { await api.post("/authz/custom-roles", { label, copy_from: copy === "none" ? null : copy }); toast.success("Role created"); setLabel(""); onAdded(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-white border rounded-xl p-4 flex flex-wrap gap-2 items-end" data-testid="authz-new-role">
      <div className="flex-1 min-w-[200px]"><p className="text-xs text-slate-500 mb-1">New role name</p><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Front desk" data-testid="authz-new-role-name" /></div>
      <div><p className="text-xs text-slate-500 mb-1">Start from</p>
        <Select value={copy} onValueChange={setCopy}><SelectTrigger className="w-60" data-testid="authz-new-role-copy"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="none">Read-only basics</SelectItem>{roles.map((r) => <SelectItem key={r.key} value={r.key}>Copy of {r.label}</SelectItem>)}</SelectContent></Select></div>
      <Button className="btn-brand" disabled={label.trim().length < 2} onClick={add} data-testid="authz-new-role-add"><Plus className="w-4 h-4 mr-1" />Add role</Button>
    </div>
  );
}

function Cell({ role, perm, on, toggle }) {
  const locked = role.locked.includes(perm);
  if (locked) return <span className="inline-flex items-center gap-1 text-xs text-slate-500" title="Always on so admins cannot lock themselves out" data-testid={`authz-cell-${role.key}-${perm}`}><Lock className="w-3.5 h-3.5" />On</span>;
  return <Checkbox checked={on} onCheckedChange={() => toggle(role.key, perm)} aria-label={`${role.label}: ${perm}`} data-testid={`authz-cell-${role.key}-${perm}`} />;
}

function RoleHead({ r, count, dirty, onReset, onDelete }) {
  return (
    <th className="px-2 py-2 text-left align-bottom min-w-[120px] font-semibold text-xs" data-testid={`authz-role-${r.key}`}>
      <span className="block leading-tight">{r.label}{dirty && <span className="text-amber-600"> *</span>}</span>
      <span className="block font-normal text-slate-500">{count || 0} active - {r.builtin ? (r.customized ? "customized" : "default") : "custom"}</span>
      <span className="flex gap-1 mt-1">
        {r.builtin && r.customized && <button className="text-[11px] underline text-slate-600 flex items-center" onClick={onReset} data-testid={`authz-reset-${r.key}`}><RotateCcw className="w-3 h-3 mr-0.5" />Default</button>}
        {!r.builtin && <button className="text-[11px] underline text-red-700 flex items-center" onClick={onDelete} data-testid={`authz-delete-${r.key}`}><Trash2 className="w-3 h-3 mr-0.5" />Delete</button>}
      </span>
    </th>
  );
}

function ProviderGrants({ d, onDone }) {
  const set = async (m, on) => {
    const perms = on ? [...new Set([...m.grants, "default_packet:own"])] : m.grants.filter((x) => x !== "default_packet:own");
    try { await api.put(`/authz/members/${m.id}/grants`, { perms }); toast.success(on ? `${m.name} can now set their own default packet` : `${m.name} uses facility defaults`); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  const roleOn = d.roles.find((r) => r.key === "provider")?.perms.includes("default_packet:own");
  return (
    <div className="bg-white border rounded-xl p-4" data-testid="authz-provider-grants">
      <h3 className="font-semibold">Individual provider access</h3>
      <p className="text-sm text-slate-600">Turn on <b>"Set my own default packet"</b> for specific providers only. It is off for everyone by default.{roleOn && " It is currently also on for the whole Provider role above."}</p>
      <div className="divide-y mt-3">{d.provider_members.map((m) => (
        <label key={m.id} className="flex items-center gap-3 py-2 text-sm cursor-pointer" data-testid={`authz-grant-row-${m.id}`}>
          <Checkbox checked={m.grants.includes("default_packet:own")} onCheckedChange={(v) => set(m, v === true)} data-testid={`authz-grant-${m.id}`} />
          <span className="flex-1"><b>{m.name}</b> <span className="text-slate-500">{m.provider ? `- ${m.provider}` : "- not linked to a provider record"}</span>
            {m.has_own_default && !m.grants.includes("default_packet:own") && <span className="block text-xs text-amber-700" data-testid={`authz-grant-stale-${m.id}`}>Still has a saved default packet - clear it in Admin &gt; Default packets if it should no longer apply.</span>}</span>
          <span className="text-xs text-slate-500">Set my own default packet</span>
        </label>))}
        {!d.provider_members.length && <p className="text-sm text-slate-500 py-2">No provider members yet.</p>}</div>
    </div>
  );
}

export default function AuthorizationManager() {
  const { reloadPractice } = usePractice();
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState({});
  const load = () => api.get("/authz").then((r) => { setD(r.data); setEdit(Object.fromEntries(r.data.roles.map((x) => [x.key, new Set(x.perms)]))); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const screens = useMemo(() => (d ? [...new Set(d.catalog.map((c) => c.screen))] : []), [d]);
  if (!d) return <p className="text-sm text-slate-500">Loading...</p>;
  const dirtyKeys = d.roles.filter((r) => [...edit[r.key]].sort().join() !== [...r.perms].sort().join()).map((r) => r.key);
  const toggle = (k, p) => { const s = new Set(edit[k]); s.has(p) ? s.delete(p) : s.add(p); setEdit({ ...edit, [k]: s }); };
  const done = async () => { await load(); reloadPractice(); };
  const save = async () => {
    try { for (const k of dirtyKeys) await api.put(`/authz/roles/${k}`, { perms: [...edit[k]] }); toast.success("Permissions saved (audited)"); done(); } catch (e) { toast.error(errMsg(e)); }
  };
  const act = (fn, msg) => async () => { try { await fn(); toast.success(msg); done(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4" data-testid="authorization-manager">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px]"><h2 className="text-lg font-semibold">Authorization Manager</h2>
          <p className="text-sm text-slate-600">Turn each screen and action on or off per role for this organization. Changes apply on the next page load and are recorded in the audit log.</p></div>
        <Button variant="outline" disabled={!dirtyKeys.length} onClick={() => setEdit(Object.fromEntries(d.roles.map((x) => [x.key, new Set(x.perms)])))} data-testid="authz-discard">Discard</Button>
        <Button className="btn-brand" disabled={!dirtyKeys.length} onClick={save} data-testid="authz-save">Save changes{dirtyKeys.length ? ` (${dirtyKeys.length})` : ""}</Button>
      </div>
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="authz-matrix">
          <thead className="bg-slate-50 border-b"><tr><th className="px-3 py-2 text-left min-w-[260px] sticky left-0 bg-slate-50">Screen / function</th>
            {d.roles.map((r) => <RoleHead key={r.key} r={r} count={d.member_counts[r.key]} dirty={dirtyKeys.includes(r.key)}
              onReset={act(() => api.post(`/authz/roles/${r.key}/reset`), `${r.label} reset to default`)}
              onDelete={act(() => api.delete(`/authz/custom-roles/${r.key}`), `${r.label} deleted`)} />)}</tr></thead>
          <tbody>{screens.map((s) => [
            <tr key={s} className="bg-[var(--brand-soft)]"><td colSpan={d.roles.length + 1} className="px-3 py-1.5 font-semibold text-xs uppercase tracking-wide sticky left-0">{s}</td></tr>,
            ...d.catalog.filter((c) => c.screen === s).map((c) => (
              <tr key={c.perm} className="border-b last:border-0 hover:bg-slate-50" data-testid={`authz-row-${c.perm}`}>
                <td className="px-3 py-2 sticky left-0 bg-white"><span className="block">{c.label}</span><span className="font-mono text-[10px] text-slate-400">{c.perm}</span></td>
                {d.roles.map((r) => <td key={r.key} className="px-2 py-2"><Cell role={r} perm={c.perm} on={edit[r.key].has(c.perm)} toggle={toggle} /></td>)}
              </tr>)),
          ])}</tbody>
        </table>
      </div>
      <ProviderGrants d={d} onDone={done} />
      <NewRole roles={d.roles} onAdded={done} />
      <p className="text-xs text-slate-500">Providers and regional directors still only see patients in their own facilities / group. Custom roles see the whole organization. Signed-in staff pick up changes when they refresh.</p>
    </div>
  );
}
